<script setup lang="ts">
import { ref, watch } from 'vue'
import { Button } from '@/components/ui/button'
import { Plus, Trash2, RefreshCw, LayoutTemplate } from 'lucide-vue-next'
import BaseVisualEditor from './BaseVisualEditor.vue'
import MermaidCodeEditor from './MermaidCodeEditor.vue'
import MermaidLivePreview from './MermaidLivePreview.vue'

const props = defineProps<{
  open: boolean
}>()

const emit = defineEmits<{
  'update:open': [value: boolean]
  'save': [code: string]
}>()

const showCode = ref(true)
const copied = ref(false)
const editableCode = ref('')

type EMType = 'ui' | 'cmd' | 'evt' | 'view'

interface EMItem {
  id: string
  type: EMType
  name: string
}

const items = ref<EMItem[]>([
  { id: crypto.randomUUID(), type: 'ui', name: 'UserUI' },
  { id: crypto.randomUUID(), type: 'cmd', name: 'RegisterUser' },
  { id: crypto.randomUUID(), type: 'evt', name: 'UserRegistered' },
  { id: crypto.randomUUID(), type: 'view', name: 'UserList' }
])

const generateId = () => crypto.randomUUID()

const addItem = () => {
  items.value.push({
    id: generateId(),
    type: 'evt',
    name: `NewEvent`
  })
}

const removeItem = (idx: number) => {
  items.value.splice(idx, 1)
}

const moveItem = (idx: number, dir: 1 | -1) => {
  if (idx + dir < 0 || idx + dir >= items.value.length) return
  const temp = items.value[idx]
  items.value[idx] = items.value[idx + dir]
  items.value[idx + dir] = temp
}

const generateMermaid = () => {
  let code = `eventmodeling\n`
  
  items.value.forEach((item, index) => {
    const tf = (index + 1).toString().padStart(2, '0')
    const safeName = item.name.replace(/[^a-zA-Z0-9_]/g, '') || `Item${tf}`
    code += `  timeframe ${tf} ${item.type} ${safeName}\n`
  })
  
  editableCode.value = code
}

const syncToVisual = () => {
  const code = editableCode.value
  const lines = code.split('\n')
  const newItems: EMItem[] = []
  
  const tfRegex = /^(?:tf|timeframe)\s+([a-zA-Z0-9_]+)\s+(ui|cmd|evt|view)\s+(.*)$/
  
  for (const line of lines) {
    const t = line.trim()
    if (tfRegex.test(t)) {
      const match = t.match(tfRegex)!
      newItems.push({
        id: generateId(),
        type: match[2] as EMType,
        name: match[3].trim()
      })
    }
  }
  
  if (newItems.length > 0) {
    items.value = newItems
  }
}

watch(() => props.open, (newVal) => {
  if (newVal) {
    const editingCode = localStorage.getItem('editingDiagramCode');
    if (editingCode) {
      setTimeout(() => {
        editableCode.value = editingCode;
        localStorage.removeItem('editingDiagramCode');
        syncToVisual();
      }, 300);
      return;
    }
  }

  if (newVal && !editableCode.value) {
    generateMermaid()
  }
}, { immediate: true })

watch(items, () => {
  generateMermaid()
}, { deep: true })

const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(editableCode.value)
    copied.value = true
    setTimeout(() => copied.value = false, 2000)
  } catch (err) {
    console.error('Failed to copy text: ', err)
  }
}

const saveAndClose = () => {
  emit('save', editableCode.value)
  emit('update:open', false)
}
</script>

<template>
  <BaseVisualEditor
    :open="open"
    @update:open="$emit('update:open', $event)"
    title="Event Modeling Diagram"
    description="Diseña el flujo de información y estados del sistema sobre una línea de tiempo (Timeframes)."
    :showCode="showCode"
    @update:showCode="showCode = $event"
    :copied="copied"
    @copy="copyToClipboard"
    @save="saveAndClose"
  >
    <template #icon>
      <LayoutTemplate class="w-5 h-5 text-primary" />
    </template>

    <template #toolbar>
      <Button size="sm" variant="default" @click="addItem"><Plus class="w-4 h-4 mr-2" /> Añadir Paso (Timeframe)</Button>
    </template>

    <template #canvas>
      <div class="h-full w-full overflow-y-auto p-6 bg-slate-50 dark:bg-slate-900/50 flex flex-col gap-6">
        
        <div class="bg-card border rounded-lg p-4 shadow-sm flex flex-col gap-4">
          <h3 class="font-semibold text-lg border-b pb-2 flex items-center gap-2">
            <LayoutTemplate class="w-5 h-5 text-blue-500"/> Línea de Tiempo (Timeframes)
          </h3>
          
          <div v-if="items.length === 0" class="text-center text-muted-foreground py-4 text-xs border border-dashed rounded">
            No hay elementos. Añade tu primer paso (Timeframe).
          </div>

          <div class="flex flex-col gap-2">
            <div 
              v-for="(item, idx) in items" 
              :key="item.id"
              class="flex items-center gap-3 bg-muted/40 border p-2 rounded-md group hover:bg-muted/60 transition-colors"
            >
              <div class="flex flex-col gap-1 items-center justify-center shrink-0 w-8">
                <button 
                  @click="moveItem(idx, -1)" 
                  :disabled="idx === 0"
                  class="text-muted-foreground hover:text-primary disabled:opacity-30"
                >
                  ▲
                </button>
                <div class="text-xs font-mono font-bold">{{ (idx + 1).toString().padStart(2, '0') }}</div>
                <button 
                  @click="moveItem(idx, 1)" 
                  :disabled="idx === items.length - 1"
                  class="text-muted-foreground hover:text-primary disabled:opacity-30"
                >
                  ▼
                </button>
              </div>

              <div class="flex flex-col gap-1 w-32 shrink-0">
                <label class="text-[10px] font-bold uppercase text-muted-foreground ml-1">Tipo</label>
                <select v-model="item.type" class="bg-background border rounded px-2 py-1 text-sm focus:outline-none focus:ring-1">
                  <option value="ui">UI (Interfaz)</option>
                  <option value="cmd">Comando (Acción)</option>
                  <option value="evt">Evento (Estado)</option>
                  <option value="view">Vista (Read Model)</option>
                </select>
              </div>

              <div class="flex flex-col gap-1 flex-1">
                <label class="text-[10px] font-bold uppercase text-muted-foreground ml-1">Nombre (Sin espacios)</label>
                <input 
                  v-model="item.name" 
                  class="bg-background border rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 font-mono" 
                  placeholder="Ej. UserRegistered" 
                />
              </div>

              <button 
                @click="removeItem(idx)" 
                class="text-muted-foreground hover:bg-destructive hover:text-white p-2 rounded transition-colors"
                title="Eliminar Paso"
              >
                <Trash2 class="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #code-panel>
      <div class="text-sm font-bold p-2 bg-muted-foreground/10 text-muted-foreground flex items-center justify-between border-b shrink-0">
        Mermaid (Sincronizado)
        <Button size="sm" variant="default" class="h-7 text-xs" @click="syncToVisual">
          <RefreshCw class="w-3 h-3 mr-2" />
          Sincronizar a Visual
        </Button>
      </div>
      <MermaidCodeEditor v-model="editableCode" class="flex-1 min-h-0" />
    </template>

    <template #live-preview>
      <MermaidLivePreview :code="editableCode" />
    </template>
  </BaseVisualEditor>
</template>
